import { Component, ViewChild, ElementRef, OnDestroy, ChangeDetectorRef, ChangeDetectionStrategy, inject } from '@angular/core';

import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { FormatService } from '../../format.service';
import { InvestmentChartService } from '../../investment-chart.service';
import { validateRange } from '../../range-validate';
import { CagrCalculatorService, CagrResult, CAGR_CONSTRAINTS } from './cagr-calculator.service';

@Component({
  selector: 'app-cagr-calculator',
  standalone: true,
  imports: [FormsModule, RouterLink],
  providers: [InvestmentChartService],
  templateUrl: './cagr-calculator.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./cagr-calculator.scss']
})
export class CagrCalculator implements OnDestroy {
  fmt = inject(FormatService);
  private calc = inject(CagrCalculatorService);
  private charts = inject(InvestmentChartService);
  private cdr = inject(ChangeDetectorRef);


  @ViewChild('growthRef') growthRef!: ElementRef<HTMLCanvasElement>;

  readonly C = CAGR_CONSTRAINTS;
  readonly Math = Math;

  initialValue = 100_000;
  finalValue = 200_000;
  years = 5;

  formErrors:    Record<string, string>  = {};
  touched:       Record<string, boolean> = {};
  showFormErrors = false;

  calculated = false;
  result:     CagrResult | null = null;

  private validateForm(): boolean {
    const errors: Record<string, string> = {};
    const e1 = validateRange(this.initialValue, this.C.value.min, this.C.value.max, 'Initial value');
    if (e1) errors['initialValue'] = e1;
    const e2 = validateRange(this.finalValue, this.C.value.min, this.C.value.max, 'Final value');
    if (e2) errors['finalValue'] = e2;
    const e3 = validateRange(this.years, this.C.years.min, this.C.years.max, 'Duration');
    if (e3) errors['years'] = e3;
    else if (!Number.isInteger(Number(this.years))) errors['years'] = 'Duration must be a whole number of years.';
    this.formErrors = errors;
    return Object.keys(errors).length === 0;
  }

  get isFormValid(): boolean { return this.validateForm(); }

  onFieldChange(field: string): void {
    this.touched[field] = true;
    this.validateForm();
    if (this.calculated) {
      this.calculated = false;
      this.charts.destroyAll();
    }
  }

  onFieldBlur(field: string): void {
    this.touched[field] = true;
    if (field === 'years') this.years = Math.floor(this.years || 0);
    this.validateForm();
  }

  fieldError(field: string): string | null {
    return (this.touched[field] || this.showFormErrors) && this.formErrors[field]
      ? this.formErrors[field]
      : null;
  }

  hasError(field: string): boolean { return !!this.fieldError(field); }

  calculate(): void {
    this.showFormErrors = true;
    this.touched = { initialValue: true, finalValue: true, years: true };
    if (!this.validateForm()) return;

    this.result = this.calc.calculate(this.initialValue, this.finalValue, Math.floor(this.years));
    this.calculated = true;
    this.cdr.detectChanges();

    setTimeout(() => {
      const r = this.result!;
      this.charts.renderGrowth(
        this.growthRef.nativeElement,
        r.years.map(row => `Yr ${row.year}`),
        [
          { label: 'Value', data: r.years.map(row => Math.round(row.value)), color: '#3b82f6' }
        ]
      );
    }, 60);
  }

  reset(): void {
    this.initialValue  = 100_000;
    this.finalValue     = 200_000;
    this.years          = 5;
    this.formErrors     = {};
    this.touched        = {};
    this.showFormErrors = false;
    this.calculated     = false;
    this.result         = null;
    this.charts.destroyAll();
  }

  ngOnDestroy(): void {
    this.charts.destroyAll();
  }
}
